import { useEffect, useState, useCallback, useMemo } from "react";
import { DataTable, ColumnDef, FilterOption } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Download } from "lucide-react";
import { api, ApiClientError, PaymentProcessor } from "@/lib/api";

// Extended type with id for DataTable compatibility
type PaymentProcessorWithId = PaymentProcessor & { id: string };

const PAGE_SIZE = 50;

export function PaymentProcessorList() {
  const { logout } = useAuth();
  const [processors, setProcessors] = useState<PaymentProcessor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [_totalCount, setTotalCount] = useState<number>(0);

  const loadProcessors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const offset = (currentPage - 1) * PAGE_SIZE;
      const result = await api.paymentProcessors.list(PAGE_SIZE, offset, true);
      
      if (Array.isArray(result)) {
        setProcessors(result);
        setHasMore(result.length === PAGE_SIZE);
        setTotalCount(result.length);
      } else {
        setProcessors(result.items);
        setHasMore(result.has_next_page);
        setTotalCount(result.total);
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          logout();
        } else {
          setError(err.detail || "Failed to load payment processors");
        }
      } else {
        setError("Failed to load payment processors");
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, logout]);

  useEffect(() => {
    loadProcessors();
  }, [loadProcessors]);

  const handleNextPage = () => {
    if (!loading && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (!loading && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Transform data to include id for DataTable compatibility
  const processorsWithId = useMemo<PaymentProcessorWithId[]>(
    () => processors.map((item) => ({ ...item, id: item.processor_name })),
    [processors]
  );

  // Define columns for the Payment Processors table
  const columns: ColumnDef<PaymentProcessorWithId>[] = [
    {
      key: "processor_name",
      header: "Processor Name",
      render: (item) => (
        <div className="text-xs font-medium text-foreground">
          {item.processor_name}
        </div>
      ),
      sortValue: (item) => item.processor_name,
    },
    {
      key: "ncii_instances",
      header: "NCII Instances",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.ncii_instances}
        </div>
      ),
      sortValue: (item) => item.ncii_instances,
    },
    {
      key: "type",
      header: "Type",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.type || "N/A"}</div>
      ),
      sortValue: (item) => item.type || "",
    },
    {
      key: "region",
      header: "Region",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.region || "N/A"}</div>
      ),
      sortValue: (item) => item.region || "",
    },
    {
      key: "active_urls",
      header: "Active URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.active_urls}</div>
      ),
      sortValue: (item) => item.active_urls,
    },
    {
      key: "removed_urls",
      header: "Removed URLs",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.removed_urls}</div>
      ),
      sortValue: (item) => item.removed_urls,
    },
    {
      key: "takedown_rate",
      header: "Takedown Rate",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {(item.takedown_rate * 100).toFixed(1)}%
        </div>
      ),
      sortValue: (item) => item.takedown_rate,
    },
    {
      key: "avg_response_time",
      header: "Avg Response Time",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.avg_response_time ? `${item.avg_response_time.toFixed(1)} days` : "N/A"}
        </div>
      ),
      sortValue: (item) => item.avg_response_time || 0,
    },
    {
      key: "contacts",
      header: "Contacts",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.contacts && item.contacts.length > 0 ? item.contacts.join(", ") : "N/A"}
        </div>
      ),
      sortValue: (item) => item.contacts?.join(", ") || "",
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      render: (item) => (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-3"
          onClick={() => {
            // Read-only - no actions available
            console.log("View payment processor:", item.processor_name);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  // Define filters (for search functionality)
  const filters: FilterOption[] = [
    { label: "Type", placeholder: "All Types" },
    { label: "Region", placeholder: "All Regions" },
  ];


  // Handle export
  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export payment processors");
  };

  // Action buttons for the header
  const actionButtons = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        className="h-9 px-4"
      >
        <Download className="h-4 w-4 mr-2" />
        Export
      </Button>
      {/* Payment processors are read-only - extracted from HAR analysis */}
    </>
  );

  return (
    <DataTable
      title="Payment Processors"
      subtitle="Track payment processors enabling monetization"
      columns={columns}
      data={processorsWithId}
      loading={loading}
      error={error}
      filters={filters}
      searchPlaceholder="Search payment processors..."
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      hasNextPage={hasMore}
      hasPreviousPage={currentPage > 1}
      currentPage={currentPage}
      itemsPerPage={PAGE_SIZE}
      actionButtons={actionButtons}
      totalCount={undefined}
      entityName="payment processors"
    />
  );
}



